import { useCallback } from 'react';
import { PermissionStatus, Position } from '@capacitor/geolocation';
import * as Sentry from '@sentry/capacitor';

import { useBooleanState } from '@mopla/utils';

import { useOpenAppSettings } from './useOpenAppSettings';

type TUsePermissionsModalProps = {
	isOpen: boolean;
	askPermissionsCallback: () => Promise<any>;
	checkPermissionsCallback: () => Promise<any>;
	onPermissionsGranted:
		| VoidFunction
		| (() => Promise<void>)
		| ((arg: PermissionStatus | Position) => Promise<void>);
	onCancel: () => void;
};

export const usePermissionsModal = ({
	isOpen,
	checkPermissionsCallback,
	onCancel,
	onPermissionsGranted,
	askPermissionsCallback,
}: TUsePermissionsModalProps) => {
	const [isAccessError, openAccessError, closeAccessError] = useBooleanState();
	const [isAccessRequest, openAccessRequest, closeAccessRequest] =
		useBooleanState();
	const isRequestAccessModal = isOpen && isAccessRequest;
	const isAccessErrorModal = isOpen && isAccessError;
	const { isNativePlatform, openAppSettings } = useOpenAppSettings();

	const cancelHandler = useCallback(() => {
		closeAccessError();
		closeAccessRequest();
		onCancel();
	}, [closeAccessError, closeAccessRequest, onCancel]);

	const processPermission = useCallback(async () => {
		const permissions = await checkPermissionsCallback();

		const permissionsValues = permissions ? Object.values(permissions) : [];

		// Use else-if to make conditions mutually exclusive
		if (permissionsValues.includes('granted')) {
			onPermissionsGranted(permissions);
		} else if (
			permissionsValues.includes('denied') ||
			permissionsValues.includes('prompt-with-rationale')
		) {
			// Android returns 'prompt-with-rationale' when user previously denied permission
			// iOS returns 'denied' when user denied permission
			// Both should show the settings prompt
			openAccessError();
		} else {
			// Only show first prompt if permission has never been asked before
			openAccessRequest();
		}
	}, [
		checkPermissionsCallback,
		onPermissionsGranted,
		openAccessError,
		openAccessRequest,
	]);

	const askPermissions = useCallback(async () => {
		try {
			const permissions = await askPermissionsCallback();
			const permissionsValues = permissions ? Object.values(permissions) : [];

			// Only call onPermissionsGranted if permission was actually granted
			if (permissionsValues.includes('granted')) {
				onPermissionsGranted(permissions);
				cancelHandler();
			} else if (
				permissionsValues.includes('denied') ||
				permissionsValues.includes('prompt-with-rationale')
			) {
				// Show error modal if permission was denied
				// Android returns 'prompt-with-rationale' after user denies
				// iOS returns 'denied' after user denies
				closeAccessRequest();
				openAccessError();
			} else {
				// Permission was not granted or denied, just close
				cancelHandler();
			}
		} catch (err) {
			Sentry.captureException(err);
			cancelHandler();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const goToSettings = useCallback(() => {
		if (isNativePlatform) {
			openAppSettings();
		} else {
			askPermissions();
		}

		cancelHandler();
	}, [askPermissions, cancelHandler, isNativePlatform, openAppSettings]);

	return {
		isRequestAccessModal,
		goToSettings,
		processPermission,
		askPermissions,
		cancelHandler,
		isAccessErrorModal,
	};
};
